import {
  useGetOrganizationByUserIdLazyQuery,
  useGetRequirementsCurrentAcademicYearByStudentIdLazyQuery,
  useGetRequirementsLazyQuery,
  useGetGraduationClassesByStudentLazyQuery,
  useGetOrgRequirementsByOrgLazyQuery,
  useGetHomeDataStudentLazyQuery,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { useEffect, useState, useMemo } from "react";
import { ROLES } from "types/user";
import { Requirement } from "types/requirement";
import { REQUIREMENT } from "helpers/constants";
import { filterFeedbackStats, getFeedbackForChart } from "utils/feedback";
import { getMetricsSinceDate, DEFAULT_METRICS_DATE_RANGE } from "pages/Metrics/metricsDateRange";
import { excludeTestOrganizationVariables } from "utils/testOrganization";

export const useRequirementsPortal = () => {
  const [getRequirementsForStudents, { data: studentData }] =
    useGetRequirementsCurrentAcademicYearByStudentIdLazyQuery();

  const [getRequirementsForCoaches, { data: coachData }] =
    useGetRequirementsLazyQuery();

  const [getOrganizationsByUserId, { data: organizationsData }] =
    useGetOrganizationByUserIdLazyQuery();

  const [getGraduationClassesByStudent, { data: graduationClassesData }] =
    useGetGraduationClassesByStudentLazyQuery();

  const [getOrgRequirements, { data: orgRequirementsData }] =
    useGetOrgRequirementsByOrgLazyQuery();

  const [getHomeDataStudent, { data: homeDataStudent }] =
    useGetHomeDataStudentLazyQuery();

  const [competencies, setCompetencies] = useState([] as Requirement[]);
  const [EPAs, setEPAs] = useState([] as Requirement[]);
  const [iters, setIters] = useState([] as Requirement[]);

  const { user } = useAuthUser();

  // Check if there's exactly one graduation_class whose name starts with the user's name
  const shouldShowRequirementsSelectorButton = useMemo(() => {
    if (!user?.name || !graduationClassesData?.graduation_class) {
      return false;
    }
    const graduationClasses = graduationClassesData.graduation_class.filter(
      (gc) => gc.name && gc.name.startsWith(user.name),
    );

    return graduationClasses.length > 0;
  }, [user?.name, graduationClassesData?.graduation_class]);

  // Get the graduation class that matches the condition
  const matchingGraduationClass = useMemo(() => {
    if (!user?.name || !graduationClassesData?.graduation_class) {
      return null;
    }
    const graduationClasses = graduationClassesData.graduation_class.filter(
      (gc) => gc.name && gc.name.startsWith(user.name),
    );
    return graduationClasses.length > 0 ? graduationClasses[0] : null;
  }, [user?.name, graduationClassesData?.graduation_class]);

  // Get organization ID and current academic year ID
  const organizationId = useMemo(() => {
    return matchingGraduationClass?.program?.org_id || null;
  }, [matchingGraduationClass]);

  const currentAcademicYearId = useMemo(() => {
    return matchingGraduationClass?.current_academic_year_id || null;
  }, [matchingGraduationClass]);

  // Get available requirements from organization
  const availableRequirements = useMemo(() => {
    if (orgRequirementsData?.organization_requirement) {
      return orgRequirementsData.organization_requirement
        .map((or) => or.requirement)
        .filter(Boolean) as Requirement[];
    }
    return [];
  }, [orgRequirementsData]);

  // Get currently selected requirements
  const currentRequirements = useMemo(() => {
    return (
      studentData?.graduation_class[0]?.current_academic_year?.requirements.map(
        (r) => r.requirement,
      ) || []
    );
  }, [studentData]);

  useEffect(() => {
    const reqs =
      studentData?.graduation_class[0]?.current_academic_year?.requirements.map(
        (r) => ({
          ...r.requirement,
          academic_year_requirement_id: r.academic_year_requirement_id,
        }),
      ) || [];
    const epas = reqs.filter(
      (req) => req?.type === REQUIREMENT.REQUIREMENT_EPA,
    );
    setEPAs(epas as Requirement[]);
    const competencies = reqs.filter(
      (req) => req?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
    );
    setCompetencies(competencies as Requirement[]);
    const iters = reqs.filter(
      (req) => req?.type === REQUIREMENT.REQUIREMENT_ITER,
    );
    setIters(iters as Requirement[]);
  }, [studentData]);

  useEffect(() => {
    const orgRequirementIds: string[] = [];
    for (const org of organizationsData?.organization || []) {
      const reqs = org.requirements.map((r) => r.requirement.requirement_id);
      for (const reqId of reqs) {
        if (!orgRequirementIds.includes(reqId)) {
          orgRequirementIds.push(reqId);
        }
      }
    }
    setEPAs(
      (coachData?.requirement.filter(
        (r) =>
          r.type === REQUIREMENT.REQUIREMENT_EPA &&
          orgRequirementIds.includes(r.requirement_id),
      ) as Requirement[]) || [],
    );
    setCompetencies(
      (coachData?.requirement.filter(
        (r) =>
          r.type === REQUIREMENT.REQUIREMENT_COMPETENCY &&
          orgRequirementIds.includes(r.requirement_id),
      ) as Requirement[]) || [],
    );
    setIters(
      (coachData?.requirement.filter(
        (r) =>
          r.type === REQUIREMENT.REQUIREMENT_ITER &&
          orgRequirementIds.includes(r.requirement_id),
      ) as Requirement[]) || [],
    );
  }, [coachData, organizationsData]);

  // Fetch graduation classes for students
  useEffect(() => {
    if (user?.role === ROLES.STUDENT && user?.user_id) {
      getGraduationClassesByStudent({
        variables: { userId: user.user_id },
      }).then();
    }
  }, [user, getGraduationClassesByStudent]);

  // Fetch home data for students (for score progress chart)
  useEffect(() => {
    if (user?.role === ROLES.STUDENT && user?.user_id) {
      getHomeDataStudent({
        variables: {
          userId: user.user_id,
          sinceDate: getMetricsSinceDate(DEFAULT_METRICS_DATE_RANGE),
          ...excludeTestOrganizationVariables,
        },
      });
    }
  }, [user, getHomeDataStudent]);

  // Build feedbackStatsByRequirementId from home data (same as home/learner detail)
  const feedbackStatsByRequirementId = useMemo(() => {
    const data = homeDataStudent;
    if (!data?.feedbackStatsData) return {};
    const feedbacksByRequirementId: Record<string, any[]> = {};
    data.feedbackStatsData.forEach((feedback: any) => {
      const rid = feedback.feedback_skills?.[0]?.requirement_id;
      if (!rid) return;
      if (!feedbacksByRequirementId[rid]) feedbacksByRequirementId[rid] = [];
      feedbacksByRequirementId[rid].push(feedback);
    });
    const result: Record<string, { totalFeedbacks: number; currentRate: number; data: any[] }> = {};
    Object.keys(feedbacksByRequirementId).forEach((key) => {
      const filteredFeedbackStats = filterFeedbackStats(
        feedbacksByRequirementId[key],
        true,
      );
      result[key] = {
        totalFeedbacks: filteredFeedbackStats.length,
        currentRate: Math.max(
          ...filteredFeedbackStats.map((stat: any) => stat.value),
          0,
        ),
        data: getFeedbackForChart(filteredFeedbackStats),
      };
    });
    // Aggregate score progress per ITER (feedbacks whose skills are all under this ITER's competencies)
    const reqs =
      data?.graduation_class?.[0]?.current_academic_year?.requirements?.map(
        (r: any) => r.requirement,
      ) || [];
    const iters = reqs.filter(
      (r: any) => r?.type === REQUIREMENT.REQUIREMENT_ITER,
    );
    iters.forEach((iter: any) => {
      const competencyIds =
        iter.competencies?.map(
          (c: any) => c.competency?.requirement_id,
        ).filter(Boolean) || [];
      if (competencyIds.length === 0) return;
      const feedbacksForIter = (data.feedbackStatsData ?? []).filter(
        (f: any) =>
          f.feedback_skills?.length > 0 &&
          f.feedback_skills.every((s: any) =>
            competencyIds.includes(s.requirement_id),
          ),
      );
      if (feedbacksForIter.length === 0) return;
      const filteredFeedbackStats = filterFeedbackStats(
        feedbacksForIter as any,
        true,
      );
      const rates = filteredFeedbackStats.map((s: any) => s.value);
      result[iter.requirement_id] = {
        totalFeedbacks: filteredFeedbackStats.length,
        currentRate: rates.length > 0 ? Math.max(...rates) : 0,
        data: getFeedbackForChart(filteredFeedbackStats),
      };
      feedbacksByRequirementId[iter.requirement_id] = feedbacksForIter;
    });
    return result;
  }, [homeDataStudent]);

  // Fetch organization requirements when organizationId is available
  useEffect(() => {
    if (shouldShowRequirementsSelectorButton && organizationId) {
      getOrgRequirements({ variables: { orgId: organizationId } });
    }
  }, [
    shouldShowRequirementsSelectorButton,
    organizationId,
    getOrgRequirements,
  ]);

  useEffect(() => {
    if (user?.role === ROLES.STUDENT) {
      getRequirementsForStudents({
        variables: { studentId: user?.user_id },
      }).then();
    } else if (user?.role === ROLES.COACH) {
      getRequirementsForCoaches().then();
      getOrganizationsByUserId({ variables: { userId: user?.user_id } }).then();
    }
  }, [user, getRequirementsForStudents, getRequirementsForCoaches]);

  return {
    EPAs,
    competencies,
    iters,
    shouldShowRequirementsSelectorButton,
    organizationId,
    availableRequirements,
    currentAcademicYearId,
    currentRequirements,
    feedbackStatsByRequirementId,
    refetchStudentRequirements: () => {
      if (user?.role === ROLES.STUDENT && user?.user_id) {
        return getRequirementsForStudents({
          variables: { studentId: user.user_id },
        });
      }
      return Promise.resolve();
    },
  };
};
